import { motion } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { useLanguage } from "@/i18n";
import { ASSETS, BOT_STATUS, WHATSAPP_URL } from "@/data";
import { PixelAvatar } from "@/components/PixelAvatar";

const BOT_ART = {
  naty: ASSETS.naty,
  hano: ASSETS.hano,
  zora: ASSETS.zora,
  momo: ASSETS.momo,
  madara: ASSETS.madara,
  aizen: ASSETS.aizen,
};

const BotCard = ({ bot, index, t }) => {
  const inquiryUrl = `${WHATSAPP_URL}?text=${encodeURIComponent(`${t.bots.inquiry} ${bot.name}`)}`;

  return (
    <motion.article
      data-testid={`bot-card-${bot.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.12 }}
      className="group relative flex flex-col overflow-hidden border border-white/10 bg-[#0A0D24]/70 transition-all duration-500 hover:-translate-y-1.5 hover:border-cyan-400/50 hover:shadow-[0_20px_60px_-20px_rgba(0,240,255,0.35)]"
    >
      <div className="relative aspect-square overflow-hidden">
        {BOT_ART[bot.id] ? (
          <img
            src={BOT_ART[bot.id]}
            alt={`Bot ${bot.name}`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="grid-bg flex h-full w-full items-center justify-center bg-[#0E1338]">
            <PixelAvatar seed={bot.name} className="h-3/5 w-3/5 transition-transform duration-700 group-hover:scale-110" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D24] via-transparent to-transparent" />
        <span
          data-testid={`bot-badge-${bot.id}`}
          className="font-pixel absolute left-3 top-3 border border-cyan-400/40 bg-[#050714]/85 px-2.5 py-1 text-[9px] tracking-[0.25em] text-cyan-300 backdrop-blur-sm"
        >
          {t.bots.byBadge}
        </span>
        {BOT_STATUS[bot.id] ? (
          <span data-testid={`bot-status-${bot.id}`} className="absolute right-3 top-3 flex items-center gap-1.5 border border-emerald-400/30 bg-[#050714]/85 px-2 py-1 backdrop-blur-sm">
            <span className="blink h-1.5 w-1.5 rounded-full bg-emerald-400" />
            <span className="font-pixel text-[8px] tracking-[0.2em] text-emerald-300">{t.bots.status}</span>
          </span>
        ) : (
          <span data-testid={`bot-status-${bot.id}`} className="absolute right-3 top-3 flex items-center gap-1.5 border border-red-400/30 bg-[#050714]/85 px-2 py-1 backdrop-blur-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
            <span className="font-pixel text-[8px] tracking-[0.2em] text-red-300">OFFLINE</span>
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col p-6">
        <span className="font-pixel text-[9px] tracking-[0.3em] text-purple-400">{bot.role}</span>
        <h3 className="font-display mt-2 text-2xl font-bold text-white">{bot.name}</h3>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{bot.desc}</p>
        <div className="mt-6 flex items-center gap-3">
          <a
            href={inquiryUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`bot-learn-more-${bot.id}`}
            className="pixel-corners-sm flex flex-1 items-center justify-center gap-2 border border-cyan-400/40 bg-cyan-400/10 px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-cyan-300 transition-all duration-300 hover:bg-cyan-400 hover:text-[#050714]"
          >
            {t.bots.learnMore}
            <ArrowUpRight className="h-4 w-4" />
          </a>
          <a
            href={inquiryUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`bot-add-${bot.id}`}
            aria-label={`${t.bots.addBot} ${bot.name}`}
            className="pixel-corners-sm flex h-10 w-10 items-center justify-center border border-purple-500/40 bg-purple-500/10 text-purple-300 transition-all duration-300 hover:bg-purple-500 hover:text-white"
          >
            <Plus className="h-5 w-5" />
          </a>
        </div>
      </div>
    </motion.article>
  );
};

export default function Bots() {
  const { t } = useLanguage();

  return (
    <section id="bots" data-testid="bots-section" className="relative scroll-mt-20 py-24 sm:py-32">
      <div className="pointer-events-none absolute left-1/2 top-0 h-px w-2/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
        >
          <div>
            <span className="font-pixel text-[10px] tracking-[0.4em] text-cyan-400">// {t.bots.label}</span>
            <h2 className="font-display mt-4 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              {t.bots.title}
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">{t.bots.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {t.bots.list.map((bot, i) => (
            <BotCard key={bot.id} bot={bot} index={i} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
}
